import { create } from 'zustand';

interface UserStatusInfo {
  emoji: string | null;
  text: string;
  expiresAt: string | null;
}

interface NotificationSettings {
  dndUntil: string | null;
  workingHoursEnabled: boolean;
  workingHours: Record<string, { start: string; end: string }> | null;
  timezone: string;
  emailFallbackEnabled: boolean;
  digestEnabled: boolean;
}

interface UserAvailability {
  isDnd: boolean;
  isOutsideWorkingHours: boolean;
}

interface StatusState {
  statusByUserId: Record<string, UserStatusInfo>;
  myStatus: UserStatusInfo | null;
  myNotificationSettings: NotificationSettings | null;
  availabilityByUserId: Record<string, UserAvailability>;

  setUserStatus: (userId: string, status: UserStatusInfo) => void;
  clearUserStatus: (userId: string) => void;
  setMyStatus: (status: UserStatusInfo | null) => void;
  setMyNotificationSettings: (settings: NotificationSettings | null) => void;
  setAvailability: (availability: Array<{ userId: string; isDnd: boolean; isOutsideWorkingHours: boolean }>) => void;
}

export const useStatusStore = create<StatusState>((set) => ({
  statusByUserId: {},
  myStatus: null,
  myNotificationSettings: null,
  availabilityByUserId: {},

  setUserStatus: (userId, status) =>
    set((s) => ({
      statusByUserId: { ...s.statusByUserId, [userId]: status },
    })),

  clearUserStatus: (userId) =>
    set((s) => {
      const { [userId]: _, ...rest } = s.statusByUserId;
      return { statusByUserId: rest };
    }),

  setMyStatus: (status) => set({ myStatus: status }),

  setMyNotificationSettings: (settings) => set({ myNotificationSettings: settings }),

  setAvailability: (availability) =>
    set(() => {
      const map: Record<string, UserAvailability> = {};
      for (const a of availability) {
        map[a.userId] = { isDnd: a.isDnd, isOutsideWorkingHours: a.isOutsideWorkingHours };
      }
      return { availabilityByUserId: map };
    }),
}));

/**
 * Check if the current user is unavailable (DND active or outside working hours).
 * Uses the current user's notification settings from the store.
 */
export function isCurrentUserUnavailable(settings: NotificationSettings | null): boolean {
  if (!settings) return false;

  // Check DND
  if (settings.dndUntil) {
    const dndUntil = new Date(settings.dndUntil);
    if (dndUntil > new Date()) return true;
  }

  // Check working hours
  if (settings.workingHoursEnabled && settings.workingHours) {
    return !isWithinWorkingHoursClient(settings.workingHours, settings.timezone);
  }

  return false;
}

/**
 * Client-side working hours check (mirrors server-side logic).
 */
function isWithinWorkingHoursClient(
  workingHours: Record<string, { start: string; end: string }>,
  timezone: string,
): boolean {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const weekday = parts.find((p) => p.type === 'weekday')?.value?.toLowerCase();
    const hour = parts.find((p) => p.type === 'hour')?.value;
    const minute = parts.find((p) => p.type === 'minute')?.value;

    if (!weekday || !hour || !minute) return true;

    const daySchedule = workingHours[weekday];
    if (!daySchedule) return false;

    const currentMinutes = parseInt(hour) * 60 + parseInt(minute);
    const [startH, startM] = daySchedule.start.split(':').map(Number);
    const [endH, endM] = daySchedule.end.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  } catch {
    return true;
  }
}
